import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ChevronDown } from "lucide-react";
import { SocialIcons } from "@/components/SocialLinks";
import WhatsAppButton from "@/components/WhatsAppButton";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import analytics from "@/utils/analytics";
import { whatsAppUrl } from "@/utils/whatsapp";

const SOLUTIONS = [
  { label: "Card Payment Machines", to: "/card-machines" },
  { label: "Payment Gateway", to: "/payment-gateway" },
  { label: "EPOS Systems", to: "/epos-systems" },
  { label: "Online Payments", to: "/online-payments" },
  { label: "Online Ordering", to: "/online-ordering" },
  { label: "Phone Payments", to: "/virtual-terminal" },
  { label: "Merchant Cash Advance", to: "/merchant-cash-advance" },
];

const INDUSTRIES = [
  { label: "All Industries", to: "/industries" },
  { label: "Hospitality", to: "/industries/hospitality" },
  { label: "Pubs & Bars", to: "/industries/pubs" },
  { label: "Cafés & Coffee Shops", to: "/industries/cafes" },
  { label: "Takeaways", to: "/industries/takeaways" },
  { label: "Retail", to: "/industries/retail" },
  { label: "Personal Care Services", to: "/industries/personal-care" },
  { label: "E-Commerce", to: "/industries/e-commerce" },
  { label: "Fitness Training Services", to: "/industries/fitness" },
  { label: "Pet Services", to: "/industries/pet-services" },
  { label: "Beauty Therapists & Hair Salons", to: "/industries/beauty" },
];

const WORK_WITH_US = [
  { label: "Switch and Save", to: "/switch-and-save" },
  { label: "Partner Programme", to: "/partner-programme" },
  { label: "EPOS Partner Calculator", to: "/epos-partner-calculator" },
  { label: "E-commerce Partner Calculator", to: "/ecom-partner-calculator" },
  { label: "E-Commerce Integrations", to: "/partners/integrations" },
  { label: "Refer A Friend", to: "/refer-a-friend" },
];

const ABOUT = [
  { label: "Our Story", to: "/about" },
  { label: "Purpose Beyond Payments", to: "/purpose-beyond-payments" },
  { label: "Meet The Team", to: "/about/team" },
  { label: "Our Experience", to: "/about/experience" },
  { label: "Charity Support", to: "/about/charity" },
  { label: "Sustainability", to: "/about/sustainability" },
  { label: "Our Values", to: "/about/values" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

const NAV_SECTIONS = [
  { label: "Solutions", items: SOLUTIONS },
  { label: "Industries", items: INDUSTRIES },
  { label: "Work With Us", items: WORK_WITH_US },
  { label: "About", items: ABOUT },
];

// At lg these three collapse into a single "More" dropdown so the 7-item
// row fits; at xl+ they render inline (see DesktopNav).
const MORE_LINKS = [
  { label: "Tools", to: "/tools" },
  { label: "Pricing", to: "/pricing" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Guides", to: "/guides" },
];

function Dropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const isActive = items.some((i) => location.pathname === i.to);

  const handleClickOutside = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) setOpen(false);
  }, [ref, setOpen]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className={`flex items-center gap-1 text-sm font-semibold transition-colors hover:text-brand-aqua whitespace-nowrap ${isActive ? "text-brand-aqua" : "text-brand-black"}`} data-testid={testId}>
        {/* No literal space after {label}: an adjacent " " text node merges
            with the label in the react-snap snapshot and breaks hydration
            (React #418) on every page. The flex gap provides the spacing. */}
        {label}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-60 bg-white border border-brand-light-grey rounded-xl shadow-lg py-2 z-50 max-h-[70vh] overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {items.map((item) => (
            <Link key={item.to} to={item.to} className={`block px-4 py-2.5 text-sm transition-colors ${location.pathname === item.to ? "text-brand-aqua bg-brand-aqua-light" : "text-brand-black hover:bg-gray-50 hover:text-brand-aqua"}`}>{item.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

function DesktopNav({ location }) {
  return (
    <div className="hidden lg:flex items-center gap-6">
      {NAV_SECTIONS.map((section) => (
        <Dropdown key={section.label} label={section.label} items={section.items} testId={`nav-${section.label.toLowerCase().replace(/\s/g, '-')}`} />
      ))}
      {/* lg only: Tools/Pricing/Case Studies/Guides collapse into a More dropdown */}
      <div className="xl:hidden">
        <Dropdown label="More" items={MORE_LINKS} testId="nav-more" />
      </div>
      {/* xl+: full inline row */}
      <div className="hidden xl:flex items-center gap-6">
        <Link to="/tools" className={`text-sm font-semibold transition-colors hover:text-brand-aqua whitespace-nowrap ${location.pathname.startsWith("/tools") ? "text-brand-aqua" : "text-brand-black"}`} data-testid="nav-tools">Tools</Link>
        <Link to="/pricing" className={`text-sm font-semibold transition-colors hover:text-brand-aqua whitespace-nowrap ${location.pathname === "/pricing" ? "text-brand-aqua" : "text-brand-black"}`} data-testid="nav-pricing">Pricing</Link>
        <Link to="/case-studies" className={`text-sm font-semibold transition-colors hover:text-brand-aqua whitespace-nowrap ${location.pathname.startsWith("/case-studies") ? "text-brand-aqua" : "text-brand-black"}`}>Case Studies</Link>
        <Link to="/guides" className={`text-sm font-semibold transition-colors hover:text-brand-aqua whitespace-nowrap ${location.pathname.startsWith("/guides") ? "text-brand-aqua" : "text-brand-black"}`} data-testid="nav-guides">Guides</Link>
      </div>
    </div>
  );
}

function MobileMenu({ mobileSection, setMobileSection }) {
  return (
    <div className="lg:hidden bg-white border-t border-brand-light-grey shadow-lg max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
      <div className="px-4 py-4 space-y-1">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label}>
            <button onClick={() => setMobileSection(mobileSection === section.label ? null : section.label)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-brand-black">
              {section.label}
              <ChevronDown size={14} className={`transition-transform ${mobileSection === section.label ? "rotate-180" : ""}`} />
            </button>
            {mobileSection === section.label && (
              <div className="pl-4 space-y-1">{section.items.map((item) => (<Link key={item.to} to={item.to} className="block px-4 py-2.5 text-sm text-brand-grey hover:text-brand-aqua">{item.label}</Link>))}</div>
            )}
          </div>
        ))}
        <Link to="/tools" className="block px-4 py-3 text-sm font-semibold text-brand-aqua">Free Tools</Link>
        <Link to="/pricing" className="block px-4 py-3 text-sm font-semibold text-brand-black">Pricing</Link>
        <Link to="/case-studies" className="block px-4 py-3 text-sm font-semibold text-brand-black">Case Studies</Link>
        <Link to="/guides" className="block px-4 py-3 text-sm font-semibold text-brand-black">Guides</Link>
        <Link to="/faqs" className="block px-4 py-3 text-sm font-semibold text-brand-black">FAQs</Link>
        <div className="pt-2 border-t border-brand-light-grey space-y-2">
          <Link to="/book" className="block px-4 py-3 text-sm font-semibold text-brand-aqua">Book a Call</Link>
          <a href="tel:+448003682989" className="flex items-center gap-2 px-4 py-3 text-sm text-brand-black"><Phone size={16} /> 0800 368 2989</a>
          <WhatsAppButton variant="link" placement="navbar" label="WhatsApp us" iconSize={16} className="flex items-center gap-2 px-4 py-3 text-sm text-brand-black hover:text-brand-aqua" />
          <Link to="/switch-and-save#calculator" className="block text-center cta-primary text-white font-semibold rounded-full px-6 py-3 text-sm transition-all" data-testid="mobile-cta-button">See How Much I&apos;d Save</Link>
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const location = useLocation();

  const handleScroll = useCallback(() => setScrolled(window.scrollY > 20), []);  
  useEffect(() => { window.addEventListener("scroll", handleScroll, { passive: true }); return () => window.removeEventListener("scroll", handleScroll); }, [handleScroll]);
  useEffect(() => {
    if (open) {
      setOpen(false);
      setMobileSection(null);
      // Delay scroll to let menu close and new page render
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      });
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="hidden md:block bg-brand-black text-white text-sm py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end items-center gap-6">
          <SocialIcons variant="topbar" />
          <span className="w-px h-3.5 bg-white/20" />
          <a href="tel:+448003682989" className="flex items-center gap-1.5 hover:text-brand-aqua transition-colors" data-testid="topbar-phone"><Phone size={14} /> 0800 368 2989</a>
          <WhatsAppButton variant="link" placement="navbar" label="WhatsApp" iconSize={14} className="text-xs text-white/90 hover:text-brand-aqua" />
          <a href="mailto:hello@CanDoPayments.com" className="flex items-center gap-1.5 hover:text-brand-aqua transition-colors" data-testid="topbar-email"><Mail size={14} /> hello@CanDoPayments.com</a>
        </div>
      </div>
      <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "glass-nav shadow-md border-b border-brand-light-grey/50" : "bg-white/90 backdrop-blur-sm border-b border-brand-light-grey/30"}`} data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src="https://cdn.candopayments.com/logos/cando_long_colour_web_transparent.svg"
                alt="CanDo Payments"
                width="1217"
                height="165"
                className="h-6 md:hidden w-auto"
              />
              <img
                src="https://cdn.candopayments.com/logos/cando_square_colour_web_transparent.svg"
                alt="CanDo Payments"
                width="256"
                height="256"
                className="hidden md:block md:h-9 lg:h-10 w-auto"
                data-testid="nav-logo"
              />
            </Link>
            <DesktopNav location={location} />
            <div className="hidden lg:flex items-center gap-4">
              <a href="tel:+448003682989" className="flex items-center gap-1.5 text-sm font-bold text-brand-aqua hover:text-brand-aqua-dark transition-colors whitespace-nowrap" data-testid="nav-phone"><Phone size={15} /> 0800 368 2989</a>
              <Link to="/book" className="text-sm font-semibold text-brand-black hover:text-brand-aqua transition-colors whitespace-nowrap" data-testid="nav-book-call">Book a Call</Link>
              <Link to="/switch-and-save#calculator" className="cta-primary text-white font-semibold rounded-full px-6 py-2.5 text-sm transition-all whitespace-nowrap" data-testid="nav-cta-button">See How Much I&apos;d Save</Link>
            </div>
            <div className="lg:hidden flex items-center gap-1">
              <a href="tel:+448003682989" className="p-2 rounded-lg hover:bg-brand-aqua-light transition-colors" data-testid="mobile-phone-icon" aria-label="Call us"><Phone size={20} className="text-brand-aqua" /></a>
              <a
                href={whatsAppUrl("navbar")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.clickWhatsApp("navbar_mobile")}
                className="p-2 rounded-lg hover:bg-brand-aqua-light transition-colors"
                data-testid="mobile-whatsapp-icon"
                aria-label="Chat with us on WhatsApp"
              >
                <WhatsAppIcon size={20} className="text-brand-whatsapp" />
              </a>
              <button onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-brand-aqua-light transition-colors" data-testid="mobile-menu-toggle" aria-label="Toggle menu">
                {open ? <X size={24} className="text-brand-black" /> : <Menu size={24} className="text-brand-black" />}
              </button>
            </div>
          </div>
        </div>
        {open && <MobileMenu mobileSection={mobileSection} setMobileSection={setMobileSection} />}
      </nav>
    </>
  );
}
